import type {GeolocationFix, GpsSource} from "@/src/modules/core/types/geolocation";

const CACHE_KEY = "geolocation-fix:v1";
export const GEOLOCATION_CACHE_TTL_MS = 30 * 60 * 1000;

type CachedGeolocationFix = {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  timestamp: number;
  source: GpsSource;
};

function isCachedFix(value: unknown): value is CachedGeolocationFix {
  if (!value || typeof value !== "object") {
    return false;
  }

  const record = value as Record<string, unknown>;
  return (
    typeof record.latitude === "number" &&
    typeof record.longitude === "number" &&
    (record.accuracy === null || typeof record.accuracy === "number") &&
    typeof record.timestamp === "number" &&
    typeof record.source === "string"
  );
}

export function loadGeolocationCache(now = Date.now()): GeolocationFix | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    if (!isCachedFix(parsed)) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }

    if (now - parsed.timestamp > GEOLOCATION_CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }

    return {
      latitude: parsed.latitude,
      longitude: parsed.longitude,
      accuracy: parsed.accuracy,
      timestamp: parsed.timestamp,
      source: parsed.source === "device" ? "cache" : parsed.source,
    };
  } catch {
    return null;
  }
}

export function saveGeolocationCache(fix: GeolocationFix): void {
  try {
    const payload: CachedGeolocationFix = {
      latitude: fix.latitude,
      longitude: fix.longitude,
      accuracy: fix.accuracy,
      timestamp: fix.timestamp,
      source: fix.source,
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable
  }
}

export function clearGeolocationCache(): void {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}
